/**
 * BenchmarkComparisonsPage — `/reporting/benchmarks`.
 *
 * Wired to GET /v1/reporting/benchmarks?outletId=… on service-reporting-api.
 * The endpoint computes outlet/peer/national values from real reporting data
 * (report_run + scheduled_report counts).
 */
import { type JSX, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BarChart2 } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getBenchmarks,
  type BenchmarkResponseWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

function MiniBars({
  outlet,
  peer,
  national,
}: { outlet: number; peer: number; national: number }): JSX.Element {
  const max = Math.max(1, outlet, peer, national);
  const w = (v: number): string => `${Math.round((v / max) * 100)}%`;
  return (
    <div className="space-y-1 min-w-[120px]">
      <div className="flex items-center gap-2 text-[10px]">
        <span className="w-12 text-muted">Outlet</span>
        <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden">
          <div className="bg-nipost-green h-2" style={{ width: w(outlet) }} />
        </div>
      </div>
      <div className="flex items-center gap-2 text-[10px]">
        <span className="w-12 text-muted">Peer</span>
        <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden">
          <div className="bg-blue-400 h-2" style={{ width: w(peer) }} />
        </div>
      </div>
      <div className="flex items-center gap-2 text-[10px]">
        <span className="w-12 text-muted">Nat'l</span>
        <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden">
          <div className="bg-gray-400 h-2" style={{ width: w(national) }} />
        </div>
      </div>
    </div>
  );
}

export function BenchmarkComparisonsPage(): JSX.Element {
  const [outletId, setOutletId] = useState('');

  const compare = useMutation<BenchmarkResponseWire, Error>({
    mutationFn: () => getBenchmarks(outletId.trim()),
  });

  const denied =
    compare.isError && compare.error instanceof ApiError &&
    (compare.error.status === 401 || compare.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Benchmark comparisons"
        description="Compare an outlet against peer group + national average."
      />

      <div className="bg-white border border-border rounded-xl p-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (outletId.trim().length > 0) compare.mutate();
          }}
          className="flex items-center gap-3"
        >
          <input
            type="text"
            value={outletId}
            onChange={(e) => setOutletId(e.target.value)}
            placeholder="Outlet ID (e.g. op-1)"
            aria-label="Outlet ID"
            data-testid="benchmark-outlet-input"
            className="px-3 py-2 text-sm border border-border rounded-lg flex-1"
          />
          <button
            type="submit"
            disabled={compare.isPending || outletId.trim().length === 0}
            data-testid="benchmark-compare-btn"
            className="px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            {compare.isPending ? 'Comparing…' : 'Compare'}
          </button>
        </form>
      </div>

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/readBenchmarks</code> required.
        </DeferredNotice>
      )}

      {compare.isError && !denied && (
        <DeferredNotice title="Benchmark fetch failed">
          {compare.error.message}
        </DeferredNotice>
      )}

      {compare.data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <BarChart2 className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">
              Benchmarks for <code className="font-mono">{compare.data.outletId}</code>
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="benchmark-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Metric</th>
                  <th className="text-right px-4 py-2">Outlet</th>
                  <th className="text-right px-4 py-2">Peer</th>
                  <th className="text-right px-4 py-2">National</th>
                  <th className="text-left px-4 py-2 w-1/3">Comparison</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {compare.data.rows.map((row) => (
                  <tr key={row.metric} data-testid="benchmark-row">
                    <td className="px-4 py-2 text-xs">{row.metric}</td>
                    <td className="px-4 py-2 text-right text-xs font-medium text-nipost-green">{row.outlet.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right text-xs">{row.peer.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right text-xs">{row.national.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2"><MiniBars outlet={row.outlet} peer={row.peer} national={row.national} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
